import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Megaphone, Globe, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const FIELDS = [
  { key: "announcement_banner", label: "Announcement banner", hint: "Site-wide bar at the very top. Leave blank to hide.", live: true, rows: 2 },
  { key: "footer_note", label: "Footer note", hint: "Shown above the copyright line in the footer.", live: true, rows: 2 },
  { key: "hero_headline", label: "Home hero headline", hint: "Main headline on the home page.", rows: 2 },
  { key: "hero_subtext", label: "Home hero subtext", hint: "Supporting line under the hero headline.", rows: 2 },
  { key: "about_intro", label: "About page intro", hint: "Opening paragraph on the About page.", rows: 3 },
];

const EDITIONS = [
  { id: "global", label: "AlumniPulse (Global)", icon: Globe },
  { id: "india", label: "IndiaAlumni", icon: MapPin },
];

export default function AdminSiteContent() {
  const [edition, setEdition] = useState("global");
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (ed) => {
    setLoading(true);
    api.get(`/admin/site-content?edition=${ed}`).then((r) => setContent(r.data.content || {})).catch(() => toast.error("Could not load")).finally(() => setLoading(false));
  };
  useEffect(() => { load(edition); }, [edition]);

  const save = async (key) => {
    setSaving(key);
    try {
      await api.put(`/admin/site-content/${key}?edition=${edition}`, { value: content[key] || "" });
      toast.success("Saved — live on the site");
    } catch { toast.error("Save failed"); }
    finally { setSaving(""); }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10" data-testid="admin-site-content">
      <div className="flex items-center gap-2"><Megaphone size={22} weight="duotone" className="text-secondary" /><h1 className="font-heading text-2xl font-semibold">Site Content</h1></div>
      <p className="mt-1 text-sm text-muted-foreground">Edit the copy for each site. Changes are saved per edition and go live immediately.</p>

      <div className="mt-5 flex gap-2" data-testid="edition-switch">
        {EDITIONS.map((e) => (
          <button key={e.id} onClick={() => setEdition(e.id)} data-testid={`edition-${e.id}`}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${edition === e.id ? "border-secondary bg-secondary text-secondary-foreground" : "border-border text-muted-foreground hover:border-secondary/50"}`}>
            <e.icon size={15} /> {e.label}
          </button>
        ))}
      </div>

      {loading ? <p className="mt-8 text-sm text-muted-foreground">Loading…</p> : (
        <div className="mt-6 space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="rounded-2xl border border-border/60 p-4" data-testid={`field-${f.key}`}>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">{f.label} {f.live && <span className="ml-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] text-green-700">Live</span>}</Label>
                <Button size="sm" variant="outline" className="rounded-full gap-1.5" disabled={saving === f.key} onClick={() => save(f.key)} data-testid={`save-${f.key}`}>
                  <FloppyDisk size={14} /> {saving === f.key ? "Saving…" : "Save"}
                </Button>
              </div>
              <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{f.hint}</p>
              {f.rows > 2
                ? <Textarea rows={f.rows} value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} data-testid={`input-${f.key}`} />
                : <Input value={content[f.key] || ""} onChange={(e) => setContent({ ...content, [f.key]: e.target.value })} data-testid={`input-${f.key}`} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
